import { ArrowRight } from 'lucide-react';
import { Product } from '@/types/products/product';

type ProductCardProps = {
  product: Product;
};

const ProductCard = ({ product }: ProductCardProps) => {
  const Icon = product.meta.icon;
  const highlightedFeatures = product.features.filter(f => f.highlight).slice(0, 4);

  return (
    <a
      href={`/products/${product.meta.slug}`}
      className="block bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group overflow-hidden"
    >
      <div className="flex flex-col lg:flex-row">
        {/* Image */}
        <div className="w-full lg:w-1/4 h-48 lg:h-auto flex-shrink-0 relative overflow-hidden bg-gradient-to-br from-purple-600 to-blue-600">
          {product.meta.heroImage ? (
            <>
              <img
                src={product.meta.heroImage}
                alt={product.meta.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-pink-500/15 to-blue-600/30" />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              {Icon && <Icon className="w-20 h-20 text-white/80" />}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-6 lg:p-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex-shrink-0">
              {Icon && <Icon className="w-8 h-8 text-purple-600" />}
            </div>
            <div className="flex-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors mb-2">
                {product.meta.name}
              </h3>
              <p className="text-purple-600 font-medium">
                {product.meta.tagline}
              </p>
            </div>
          </div>

          <p className="text-gray-600 mb-4">
            {product.meta.description}
          </p>

          {/* Key Features */}
          {highlightedFeatures.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {highlightedFeatures.map((feature) => (
                  <span key={feature.id} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-purple-600">✓</span>
                    {feature.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metrics & CTA */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {product.metrics && (
              <div className="flex gap-6">
                {product.metrics.setupTime && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Setup</p>
                    <p className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                      {product.metrics.setupTime}
                    </p>
                  </div>
                )}
                {product.metrics.availability && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Availability</p>
                    <p className="font-bold text-gray-900">{product.metrics.availability}</p>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 text-purple-600 font-semibold group-hover:gap-3 transition-all">
              Learn More
              <ArrowRight className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

export default ProductCard;
